import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
  AuthenticatedResult,
  AuthStateResult,
  EventTypes,
  OidcClientNotification,
  PublicEventsService,
  UserDataResult,
  OidcSecurityService
} from 'angular-auth-oidc-client';
import {
  Observable,
  Subscription,
  switchMap,
  takeUntil,
  filter,
  Subject,
  distinctUntilKeyChanged
} from 'rxjs';
import {
  SecurityConfig,
  SecurityConfigInjectionToken
} from '../../configuration/security.config';
import { OidcProvider } from '../../providers/oidc-provider.interface';
import { AuthenticationService } from '../../services/authentication.service';
import { RedirectService } from '../../services/redirect.service';
import { UserManagementService } from '../../services/user-management.sevice';
import { User } from '../../user';
import { Jwt } from '../../web-tokens/jwt';
import { WellKnownClaims } from '../../web-tokens/well-known-claims';

@Injectable({ providedIn: 'root' })
export class OidcAuthenticationService
  extends AuthenticationService
  implements OnDestroy, OidcProvider
{
  private _isAuthenticated = false;
  private readonly _user: User;
  public accessToken: Jwt | string | null = null;
  public idToken: Jwt | null = null;

  private isAuthorizedSub: Subscription | null = null;
  private setupSub: Subscription | null = null;
  private authResultSub: Subscription | null = null;
  private isAuthenticated$: Observable<AuthenticatedResult>;
  private tokenExpired: boolean = false;
  userData$: Observable<UserDataResult>;
  private onDestroy$ = new Subject<boolean>();

  private logoutChannel = new BroadcastChannel("logout-channel");
  private doLogoffMessage = "doLogoff";

  constructor(
    private eventService: PublicEventsService,
    // OIDC Version 15 upgrade: renamed oidcService to oidcSecurityService to maintain consistency in project
    private oidcSecurityService: OidcSecurityService,
    @Inject(SecurityConfigInjectionToken) private secConfig: SecurityConfig,
    private userManager: UserManagementService,
    private redirectService: RedirectService,
    private httpClient: HttpClient
  ) {
    super();
    this._user = new User(
      this.userManager,
      this.secConfig.groups ? this.secConfig.groups : null
    );
    this.registerOidcComponents();
    this.listenForLogoffs();
  }

  public override login(redirectToDeepLink?: boolean, deepLink?: string): void {
    if (redirectToDeepLink) {
      this.redirectService.saveRedirectPath(deepLink);
    }

    this.oidcSecurityService.authorize();
  }

  public override refreshSession(): Observable<any> {
    // OIDC Version 15 upgrade: refreshSession has been removed
    return this.oidcSecurityService.forceRefreshSession().pipe(
      // The refreshSession command does not wait for the
      // access token to be updated prior to completing
      // so we need to switch to waiting for the user update
      switchMap(() => {
        return this.onUserUpdated.asObservable();
      })
    );
  }

  /**
   * Performs the logoff on the current browser tab,
   * and posts a 'doLogoff' message in the logout-channel 
   * for other tabs to listen to.
   */
  public override logout(): void {
    this.logoff();
    this.logoutChannel.postMessage(this.doLogoffMessage);
  }

  private logoff(): void {
    this.oidcSecurityService.isAuthenticated().subscribe(authenticated => {
      console.debug("Authentication status during logoff: "  + authenticated);
      if (authenticated) {
        const config = this.oidcSecurityService.getConfigurations()?.[0];
        if (config?.authWellknownEndpoints?.endSessionEndpoint) {
          console.debug("logging off with session endpoint");
          // OIDC Version 15 upgrade: logout will get cancelled if there is ongoing subscription for isAuthenticated$ and userData$.
          // so, end the subscription, to get the logout call
          this.onDestroy$.next(true);
          this.onDestroy$.complete();
          this.oidcSecurityService.logoff().subscribe(res => {
            console.debug('User is logged off ', res);
          });
        } else {
          this.localLogoff();
        }
      } else {
        this.localLogoff();
      }
    });
  }

  private localLogoff() {
    console.debug("attempting local logoff");
    if (this.secConfig?.configuration?.endSessionUrl) {
      let request = this.httpClient.delete(
        this.secConfig.configuration.endSessionUrl,
        {
          withCredentials: true
        }
      );
      request.subscribe({
        next: response => {
          console.info('RESPONSE', response);
          this.oidcSecurityService.logoffLocal();
        },

        error: error => {
          console.error(
            'ERROR in logoout using endSession endPoint  ',
            error
          );
          this.oidcSecurityService.logoffLocal();
        }
      });
    } else {
      this.oidcSecurityService.logoffLocal();
    }
  }



  /**
   * logout-channel event Listener to log off across multiple browser tabs
   * when a 'doLogoff' message is received
   */
  private listenForLogoffs() {
    this.logoutChannel.addEventListener("message", (event) => {
      var message = event.data;
      if (event.data === this.doLogoffMessage) {
        console.debug(message);
        this.logoff();
      }
    });
  }

  public getAccessToken(): Jwt | string | null {
    const currentConfiguration =
      this.oidcSecurityService.getConfigurations()?.[0];
    const serviceName = 'authStateService';
    const methodName = 'getAccessToken';
    // OIDC Version 15 upgrade: getToken() has been removed and is replaced by an observable getAccessToken(),
    //  it however needs to be subscribed to get the results which would mean a deep change in consumer app as well
    //  therefore, in order that this method can be used by consumer app and to avoid changes at consumer, using private method to get string

    const getToken: (currentConfiguration: any) => string =
      this.oidcSecurityService[serviceName][methodName].bind(
        this.oidcSecurityService[serviceName]
      );
    if (currentConfiguration?.configId) {
      let token = getToken(currentConfiguration);
      if (token) {
        // If we fail to parse the jwt then we will
        // have to assume it is an opaque token
        try {
          this.accessToken = new Jwt(token);
        } catch (error) {
          this.accessToken = token;
        }
      }
    }
    return this.accessToken;
  }

  public getIdToken(): Jwt | null {
    const currentConfiguration =
      this.oidcSecurityService.getConfigurations()?.[0];
    const serviceName = 'authStateService';
    const methodName = 'getIdToken';

    // OIDC Version 15 upgrade: getIdToken() has been removed and is replaced by an observable getIdToken()
    //  it however needs to be subscribed to get the results which would mean a deep change in consumer app as well
    //  therefore, in order that this method can be used by consumer app and to avoid changes at consumer, using private method to get string

    const getToken: (currentConfiguration: any) => string =
      this.oidcSecurityService[serviceName][methodName].bind(
        this.oidcSecurityService[serviceName]
      );
    if (currentConfiguration?.configId) {
      let token = getToken(currentConfiguration);
      if (token) {
        this.idToken = new Jwt(token);
        this.tokenExpired = false;
      }
    }
    return this.idToken;
  }

  redirectToLogin(deAuthenticated) {
    console.debug(
      'Authorization Updated: ' +
        (deAuthenticated ? 'Unauthorized' : 'Authorized') +
        '. User: ',
      this.user
    );

    // This will trigger if we go from an authenticated user to an unauthenticated user and autologin is enabled
    // This should not happen if silent-renew is on, but a race condition does exist
    // between initial authentication and the silent-renew logic that may trigger the condition
    if (
      deAuthenticated &&
      this.secConfig &&
      this.secConfig.configuration &&
      this.secConfig.configuration.autoLogin
    ) {
      // OIDC Version 15 upgrade: new thread added so that login gets new nonce written in storage
      setTimeout(() => {
        this.login(true);
      }, 0);
    }
  }

  updateUser(properties) {
    this.userManager.updateUser(properties);
  }
  emitUpdatedUser() {
    this.onUserUpdated.emit(this.user);
  }

  updateUserProperties(token: UserData, deAuthenticated = false) {
    if (token) {
      const properties: Map<string, any> = new Map<WellKnownClaims, any>([
        [WellKnownClaims.FirstName, token[WellKnownClaims.FirstName]],
        [WellKnownClaims.LastName, token[WellKnownClaims.LastName]],
        [WellKnownClaims.UserId, token[WellKnownClaims.UserId]],
        [WellKnownClaims.Roles, token[WellKnownClaims.Roles]],
        [WellKnownClaims.DisplayName, token[WellKnownClaims.DisplayName]],
        [WellKnownClaims.Racf, token[WellKnownClaims.Racf]],
        [WellKnownClaims.UserSource, token[WellKnownClaims.UserSource]],
        [WellKnownClaims.UserType, token[WellKnownClaims.UserType]],
        [WellKnownClaims.Title, token[WellKnownClaims.Title]],
        [WellKnownClaims.Department, token[WellKnownClaims.Department]],
        [WellKnownClaims.Email, token[WellKnownClaims.Email]],
        [WellKnownClaims.PhoneNumber, token[WellKnownClaims.PhoneNumber]],
        [WellKnownClaims.WorkAddress, token[WellKnownClaims.WorkAddress]],
        [WellKnownClaims.WorkPostalCode, token[WellKnownClaims.WorkPostalCode]],
        [WellKnownClaims.WorkState, token[WellKnownClaims.WorkState]],
        [WellKnownClaims.WorkCity, token[WellKnownClaims.WorkCity]]
      ]);
      this.updateUser(properties);
    } else {
      this.updateUser(null);
    }
    this.emitUpdatedUser();
    this.redirectToLogin(deAuthenticated);
  }
  /**
   * Does some basic initialization wiring with the back-end library to make sure everything works properly
   */
  registerOidcComponents(): void {
    // OIDC Version 15 upgrade: onAuthorizationResult is removed, however failure can be checked with notification CheckingAuthFinishedWithError
    this.authResultSub = this.eventService
      .registerForEvents()
      .pipe(
        filter(
          notification =>
            notification.type === EventTypes.CheckingAuthFinishedWithError
        )
      )
      .subscribe((value: OidcClientNotification<AuthStateResult>) => {
        console.error('Authorization failed ', value);
      });

    // OIDC Version 15 upgrade: isAuthenticated$ returning Observable<AuthenticatedResult>.
    // OIDC Version 15 upgrade: getIsAuthorized is removed
    this.isAuthorizedSub = this.oidcSecurityService.isAuthenticated$
      .pipe(takeUntil(this.onDestroy$))
      .pipe(distinctUntilKeyChanged('isAuthenticated'))
      .subscribe({
        next: ({ isAuthenticated }) => {
          // We need to ensure we do not constantly 'change' the user to the same thing
          // because authorization events are handled we only need to look for differences
          if (typeof isAuthenticated === 'boolean') {
            let deAuthenticated: boolean =
              this.isAuthenticated && !isAuthenticated;
            this._isAuthenticated = isAuthenticated;
            // OIDC Version 15 upgrade: this.onAuthorizationUpdated() is removed as we can get the userdata directly by subscribing userDat$ in new library
            // OIDC Version 15 upgrade: we can subscribe to userData$ and no need to parse it from token
            this.oidcSecurityService.userData$
              .pipe(takeUntil(this.onDestroy$))
              .pipe(distinctUntilKeyChanged('userData'))
              .subscribe({
                next: ({ userData }) => {
                  this.updateUserProperties(userData, deAuthenticated);
                },
                error: error => {
                  console.error(
                    'Error processing the userData in storage ',
                    error
                  );
                }
              });
          }
        },
        error: error => {
          console.error('Error processing authentication results ', error);
        }
      });

    // OIDC Version 15 upgrade: We can subscribe for when IdToken Expires
    this.eventService
      .registerForEvents()
      .pipe(
        filter(notification => notification.type === EventTypes.IdTokenExpired)
      )
      .subscribe(value => {
        this.tokenExpired = true;
        this.idToken = null;
      });
  }

  ngOnDestroy(): void {
    if (this.isAuthorizedSub !== null) {
      this.isAuthorizedSub.unsubscribe();
    }

    if (this.authResultSub !== null) {
      this.authResultSub.unsubscribe();
    }

    if (this.setupSub !== null) {
      this.setupSub.unsubscribe();
    }
  }

  public override get user(): User {
    return this._user;
  }

  /**
   * Gets whether or not the user is authenticated
   */
  public override get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  /**
   *  Determines if token is expired
   */
  public override isTokenExpired(): boolean {
    // OIDC Version 15 upgrade: we have already subscribed for the IdTokenExpired notification, which will update this value
    return this.tokenExpired;
  }
}

interface UserData {
  department: string;
  name: string;
  email: Array<string>;
  first_name: string;
  last_name: string;
  phone: string;
  racf: string;
  roles: Array<string>;
  job_title: string;
  user_guid: string;
  user_source: string;
  user_type: string;
  street_address: string;
  city: string;
  postal_code: string;
  state: string;
  auth_time: number;
  iat: number;
  amr: Array<string>;
  api_roles: Array<string>;
  at_hash: string;
  aud: string;
  exp: number;
  idp: string;
  iss: string;
  jti: string;
  nonce: string;
  preferred_username: string;
  sub: string;
  ver: number;
}
